import { CheckBox } from '@bambi/component-library/src/components/inputs/CheckBox/CheckBox';
import { createColumnHelper } from '@tanstack/react-table';
import { DateTime } from 'luxon';
import { useFormContext } from 'react-hook-form';

import store from 'src/app/store/store';
import { DatePickerColumnFilter } from 'src/common/datagridColumnFilters/DatePickerColumnFilter';
import { DaysOfWeekColumnFilter } from 'src/common/datagridColumnFilters/DaysOfWeekColumnFilter';
import { DriverColumnFilter } from 'src/common/datagridColumnFilters/DriverColumnFilter';
import { RouteTemplateRead } from 'src/common/external/bambi-api/routesApi';
import { DayOfTheWeekCell } from 'src/features/trip/routeTemplates/management/DayOfTheWeekCell';
import { routeTemplateActions } from 'src/features/trip/routeTemplates/routeTemplates.slice';

import { ApplyRouteTemplateForm } from '../types';

const columnHelper = createColumnHelper<RouteTemplateRead>();

function SelectColumn({ routeTemplate }: { routeTemplate: RouteTemplateRead }) {
  const form = useFormContext<ApplyRouteTemplateForm>();
  const selectedRouteTemplatesMap = form.watch('selectedRouteTemplatesMap');
  const isChecked = selectedRouteTemplatesMap[routeTemplate.id];

  return (
    <CheckBox
      checked={isChecked}
      onCheckedChange={() => {
        form.setValue(
          `selectedRouteTemplatesMap.${routeTemplate.id}`,
          !isChecked
        );
      }}
    />
  );
}

function SelectHeader({
  routeTemplates,
}: {
  routeTemplates: RouteTemplateRead[];
}) {
  const form = useFormContext<ApplyRouteTemplateForm>();
  const selectedRouteTemplatesMap = form.watch('selectedRouteTemplatesMap');
  const anyChecked = Object.values(selectedRouteTemplatesMap).some(Boolean);

  return (
    <CheckBox
      id="select-all"
      checked={anyChecked}
      onCheckedChange={() => {
        const newSelectedRouteTemplatesMap = routeTemplates.reduce(
          (acc, routeTemplate) => {
            acc[routeTemplate.id] = !anyChecked;
            return acc;
          },
          {} as Record<string, boolean>
        );

        form.setValue(
          'selectedRouteTemplatesMap',
          newSelectedRouteTemplatesMap
        );
      }}
    />
  );
}

export const columns = [
  columnHelper.display({
    id: 'select',
    header: (props) => (
      <SelectHeader
        routeTemplates={props.table
          .getRowModel()
          .rows.map((row) => row.original)}
      />
    ),
    cell: (props) => <SelectColumn routeTemplate={props.row.original} />,
    meta: {
      enableHeaderMenu: false,
    },
  }),
  columnHelper.accessor('name', {
    header: 'Name',
    meta: {
      enableHeaderMenu: false,
    },
  }),
  columnHelper.accessor('default_driver_name', {
    header: 'Driver',
    enableColumnFilter: true,
    meta: {
      filterComponent(column, open, onClose) {
        return (
          <DriverColumnFilter
            open={open}
            onClose={onClose}
            selected={store.getState().routeTemplates.filterDriver}
            setSelectedAction={routeTemplateActions.setFilterDriver}
          />
        );
      },
      filterCount() {
        return store.getState().routeTemplates.filterDriver.length;
      },
    },
  }),
  columnHelper.accessor('days_of_week', {
    header: 'Days of Week',
    cell: (props) => (
      <DayOfTheWeekCell days={props.row.original.days_of_week ?? []} />
    ),
    meta: {
      filterComponent(column, open, onClose) {
        return (
          <DaysOfWeekColumnFilter
            open={open}
            onClose={onClose}
            selected={store.getState().routeTemplates.filterDaysOfWeek}
            setSelectedAction={routeTemplateActions.setFilterDaysOfWeek}
          />
        );
      },
      filterCount() {
        return store.getState().routeTemplates.filterDaysOfWeek.length;
      },
    },
  }),
  columnHelper.accessor('range_start', {
    header: 'Start Date',
    cell: (props) => (
      <div className="text-sm text-gray-500">
        {props.row.original.range_start
          ? DateTime.fromISO(props.row.original.range_start).toFormat(
              'yyyy-MM-dd'
            )
          : '-'}
      </div>
    ),
    meta: {
      filterComponent(column, open, onClose) {
        return (
          <DatePickerColumnFilter
            open={open}
            onClose={onClose}
            setSelectedAction={routeTemplateActions.setFilterRangeStart}
          />
        );
      },
      filterCount() {
        const state = store.getState().routeTemplates;
        return state.filterRangeStart ? 1 : 0;
      },
    },
  }),
  columnHelper.accessor('range_end', {
    header: 'End Date',
    cell: (props) => (
      <div className="text-sm text-gray-500">
        {props.row.original.range_end
          ? DateTime.fromISO(props.row.original.range_end).toFormat(
              'yyyy-MM-dd'
            )
          : '-'}
      </div>
    ),
    meta: {
      filterComponent(column, open, onClose) {
        return (
          <DatePickerColumnFilter
            open={open}
            onClose={onClose}
            setSelectedAction={routeTemplateActions.setFilterRangeEnd}
          />
        );
      },
      filterCount() {
        const state = store.getState().routeTemplates;
        return state.filterRangeEnd ? 1 : 0;
      },
    },
  }),
  columnHelper.accessor('trip_template_count', {
    header: 'Trip Matchers',
    meta: {
      enableHeaderMenu: false,
    },
  }),
  columnHelper.accessor('notes', {
    header: 'Notes',
    meta: {
      cellStyle: {
        allowTextWrap: true,
        maxWidth: '20rem',
      },
      enableHeaderMenu: false,
    },
  }),
];
